import { useCallback, useEffect, useState } from 'react';

export const usePresets = (betBody, setBetBody) => {
  const [presets, setPresets] = useState([]);

  // Загрузка пресетов из localStorage при инициализации
  useEffect(() => {
    const savedPresets = localStorage.getItem('betting-presets');
    if (savedPresets) {
      try {
        setPresets(JSON.parse(savedPresets));
      } catch (error) {
        console.error('Error loading presets:', error);
      }
    }
  }, []);

  // Сохранение нового пресета
  const savePreset = useCallback(() => {
    const name = prompt('Введіть назву пресету:');
    if (name) {
      const newPreset = {
        id: Date.now(),
        name,
        config: betBody,
        createdAt: new Date().toISOString(),
      };
      const updatedPresets = [...presets, newPreset];
      setPresets(updatedPresets);
      localStorage.setItem('betting-presets', JSON.stringify(updatedPresets));
    }
  }, [betBody, presets]);

  // Загрузка пресета
  const loadPreset = useCallback(
    (preset) => {
      setBetBody(preset.config);
    },
    [setBetBody],
  );

  // Удаление пресета
  const deletePreset = useCallback(
    (presetId) => {
      if (window.confirm('Видалити цей пресет?')) {
        const updatedPresets = presets.filter((p) => p.id !== presetId);
        setPresets(updatedPresets);
        localStorage.setItem('betting-presets', JSON.stringify(updatedPresets));
      }
    },
    [presets],
  );

  // Очистка всех пресетов
  const clearAllPresets = useCallback(() => {
    if (window.confirm('Видалити всі пресети?')) {
      setPresets([]);
      localStorage.removeItem('betting-presets');
    }
  }, []);

  // Экспорт всех пресетов
  const exportAllPresets = useCallback(() => {
    const dataStr = JSON.stringify(presets, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `betting-presets-${new Date().toISOString().slice(0, 10)}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }, [presets]);

  // Импорт пресетов
  const importPresets = useCallback(
    (event, onError) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const imported = JSON.parse(e.target.result);
            if (Array.isArray(imported)) {
              // Добавляем новые ID для избежания конфликтов
              const importedWithNewIds = imported.map((preset) => ({
                ...preset,
                id: Date.now() + Math.random(),
                name: `${preset.name} (imported)`,
                createdAt: new Date().toISOString(),
              }));

              const updatedPresets = [...presets, ...importedWithNewIds];
              setPresets(updatedPresets);
              localStorage.setItem('betting-presets', JSON.stringify(updatedPresets));

              if (onError) onError('import', null);
            } else {
              throw new Error('Невірний формат файлу пресетів');
            }
          } catch (error) {
            if (onError) onError('import', 'Помилка при імпорті пресетів: ' + error.message);
          }
        };
        reader.readAsText(file);
      }
    },
    [presets],
  );

  // Дублирование пресета
  const duplicatePreset = useCallback(
    (preset) => {
      const duplicatedPreset = {
        ...preset,
        id: Date.now(),
        name: `${preset.name} (copy)`,
        createdAt: new Date().toISOString(),
      };
      const updatedPresets = [...presets, duplicatedPreset];
      setPresets(updatedPresets);
      localStorage.setItem('betting-presets', JSON.stringify(updatedPresets));
    },
    [presets],
  );

  // Переименование пресета
  const renamePreset = useCallback(
    (presetId) => {
      const preset = presets.find((p) => p.id === presetId);
      if (preset) {
        const newName = prompt('Нова назва пресету:', preset.name);
        if (newName && newName !== preset.name) {
          const updatedPresets = presets.map((p) => (p.id === presetId ? { ...p, name: newName } : p));
          setPresets(updatedPresets);
          localStorage.setItem('betting-presets', JSON.stringify(updatedPresets));
        }
      }
    },
    [presets],
  );

  return {
    // State
    presets,

    // Actions
    savePreset,
    loadPreset,
    deletePreset,
    clearAllPresets,
    exportAllPresets,
    importPresets,
    duplicatePreset,
    renamePreset,
  };
};
